import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { BarChart3, DollarSign, FileText, Briefcase, Download, TrendingUp, AlertCircle, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DatePicker } from '../components/DatePicker';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#F97316', '#3B82F6', '#22C55E', '#EAB308', '#EF4444'];

const statusColors = {
  pending: 'status-pending',
  in_progress: 'status-in_progress',
  completed: 'status-completed',
  on_hold: 'status-on_hold',
  cancelled: 'status-cancelled',
};

export const ReportsPage = () => {
  const navigate = useNavigate();
  const [jobsReport, setJobsReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [marginsReport, setMarginsReport] = useState(null);
  const [awaitingFinal, setAwaitingFinal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [jobsRes, financialRes, marginsRes, awaitingRes] = await Promise.all([
        reportsAPI.getJobsSummary(dateRange),
        reportsAPI.getFinancial(dateRange),
        reportsAPI.getMargins(dateRange),
        reportsAPI.getJobsAwaitingFinalInvoice(),
      ]);
      setJobsReport(jobsRes.data);
      setFinancialReport(financialRes.data);
      setMarginsReport(marginsRes.data);
      setAwaitingFinal(awaitingRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchReports();
  };

  const jobStatusData = jobsReport?.status_breakdown 
    ? Object.entries(jobsReport.status_breakdown).map(([name, value]) => ({ name, value }))
    : [];

  const financialData = financialReport ? [
    { name: 'Quoted', value: financialReport.total_quoted },
    { name: 'Invoiced', value: financialReport.total_invoiced },
    { name: 'Paid', value: financialReport.total_paid },
    { name: 'Outstanding', value: financialReport.outstanding },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground mt-1">Analytics and business insights</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <DatePicker
              value={dateRange.start_date}
              onChange={(d) => setDateRange({ ...dateRange, start_date: d })}
              placeholder="Start date"
              className="w-40"
              data-testid="reports-start-date"
            />
            <span className="text-muted-foreground">to</span>
            <DatePicker
              value={dateRange.end_date}
              onChange={(d) => setDateRange({ ...dateRange, end_date: d })}
              placeholder="End date"
              className="w-40"
              data-testid="reports-end-date"
            />
          </div>
          <Button onClick={handleRefresh} className="uppercase font-bold tracking-wide rounded-sm">
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <Tabs defaultValue="overview" className="space-y-6">
          {/* Awaiting Final Invoice Worklist — shows up across all tabs */}
          {awaitingFinal && awaitingFinal.count > 0 && (
            <Card className="bg-card border-yellow-500/40 rounded-sm" data-testid="awaiting-final-invoice-card">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-yellow-400">
                  <AlertCircle className="h-4 w-4" />
                  Jobs awaiting final invoice ({awaitingFinal.count})
                </CardTitle>
                <span className="text-xs text-muted-foreground">Has progress claims but no final invoice yet</span>
              </CardHeader>
              <CardContent>
                <div className="border border-border rounded-sm divide-y divide-border">
                  {awaitingFinal.jobs.map((j) => (
                    <button
                      type="button"
                      key={j.job_id}
                      onClick={() => navigate(`/jobs/${j.job_id}`)}
                      className="w-full flex items-center justify-between gap-4 px-3 py-2 hover:bg-secondary/40 text-left transition-colors"
                      data-testid={`awaiting-final-row-${j.job_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{j.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {j.client_name}{j.address ? ` · ${j.address}` : ''}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap text-sm">
                        <div className="font-mono font-bold">${j.invoiced_to_date.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
                        <div className="text-xs text-muted-foreground">
                          {j.progress_invoice_count} progress · {j.po_total ? `$${j.remaining_on_po?.toFixed(2)} left on PO` : 'no PO'}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <TabsList className="bg-card border border-border rounded-sm">
            <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-sm">Jobs</TabsTrigger>
            <TabsTrigger value="financial" className="rounded-sm">Financial</TabsTrigger>
            <TabsTrigger value="margins" className="rounded-sm" data-testid="reports-margins-tab">Margins</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Jobs</p>
                      <p className="text-2xl font-bold">{jobsReport?.total_jobs || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-400/10 rounded-sm">
                      <Briefcase className="h-6 w-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Quoted</p>
                      <p className="text-2xl font-bold font-mono">${(financialReport?.total_quoted || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-yellow-400/10 rounded-sm">
                      <FileText className="h-6 w-6 text-yellow-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Paid</p>
                      <p className="text-2xl font-bold font-mono text-green-400">${(financialReport?.total_paid || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-green-400/10 rounded-sm">
                      <DollarSign className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Outstanding</p>
                      <p className="text-2xl font-bold font-mono text-orange-400">${(financialReport?.outstanding || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-orange-400/10 rounded-sm">
                      <TrendingUp className="h-6 w-6 text-orange-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border">
                  <CardTitle>Jobs by Status</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {jobStatusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={jobStatusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {jobStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      No job data available
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border">
                  <CardTitle>Financial Overview</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {financialData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={financialData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" stroke="#94A3B8" />
                        <YAxis stroke="#94A3B8" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }}
                          formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                        />
                        <Bar dataKey="value" fill="#F97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      No financial data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border">
                <CardTitle>Jobs Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {jobsReport?.jobs?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead>Title</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Budget</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobsReport.jobs.map((job) => (
                        <TableRow key={job.id} className="border-border">
                          <TableCell className="font-medium">{job.title}</TableCell>
                          <TableCell>{job.client_name}</TableCell>
                          <TableCell className="text-muted-foreground max-w-xs truncate">{job.address}</TableCell>
                          <TableCell className="font-mono">{job.budget ? `$${job.budget.toLocaleString()}` : '-'}</TableCell>
                          <TableCell>
                            <Badge className={`${statusColors[job.status]} rounded-sm font-mono text-xs uppercase`}>
                              {job.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    No jobs found
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-yellow-400" />
                    Quotes
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Quotes</span>
                    <span className="font-bold">{financialReport?.quote_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Value</span>
                    <span className="font-mono font-bold">${(financialReport?.total_quoted || 0).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-400" />
                    Invoices
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Invoices</span>
                    <span className="font-bold">{financialReport?.invoice_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Invoiced</span>
                    <span className="font-mono font-bold">${(financialReport?.total_invoiced || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Paid</span>
                    <span className="font-mono font-bold text-green-400">${(financialReport?.total_paid || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Outstanding</span>
                    <span className="font-mono font-bold text-orange-400">${(financialReport?.outstanding || 0).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-border pt-3 mt-1 space-y-2" data-testid="tax-basis-block">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Tax basis (issued invoices only)</p>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Income (ex-GST)</span>
                      <span className="font-mono font-bold" data-testid="tax-basis-ex-gst">${(financialReport?.tax_basis_ex_gst || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GST collected</span>
                      <span className="font-mono font-bold text-yellow-400" data-testid="gst-collected">${(financialReport?.gst_collected || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Cancelled/archived invoices excluded.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-400" />
                    Purchase Orders
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total POs</span>
                    <span className="font-bold">{financialReport?.po_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Value</span>
                    <span className="font-mono font-bold">${(financialReport?.total_purchase_orders || 0).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Margins Tab */}
          <TabsContent value="margins" className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold text-primary" data-testid="margins-total-revenue">${(marginsReport?.total_revenue || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground mt-1">Invoiced (excl. cancelled)</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total Costs</p>
                  <p className="text-2xl font-bold" data-testid="margins-total-costs">${(marginsReport?.total_costs || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground mt-1">POs to contractors</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total Margin</p>
                  <p className={`text-2xl font-bold ${(marginsReport?.total_margin || 0) >= 0 ? 'text-green-400' : 'text-destructive'}`} data-testid="margins-total-margin">${(marginsReport?.total_margin || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground mt-1">Revenue − Costs</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Avg Margin %</p>
                  <p className={`text-2xl font-bold ${(marginsReport?.avg_margin_pct || 0) >= 20 ? 'text-green-400' : (marginsReport?.avg_margin_pct || 0) >= 10 ? 'text-yellow-400' : 'text-orange-400'}`} data-testid="margins-avg-margin-pct">
                    {(marginsReport?.avg_margin_pct || 0).toFixed(1)}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{marginsReport?.job_count || 0} job(s)</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Cash Collected</p>
                  <p className="text-2xl font-bold text-green-400" data-testid="margins-total-cash">${(marginsReport?.total_cash_collected || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground mt-1">Progress payments received</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className={`text-2xl font-bold ${(marginsReport?.total_outstanding || 0) > 0 ? 'text-orange-400' : 'text-muted-foreground'}`} data-testid="margins-total-outstanding">${(marginsReport?.total_outstanding || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground mt-1">Revenue − Cash</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border">
                <CardTitle>Per-Job Margins</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="margins-table">
                    <thead className="bg-muted/30 text-muted-foreground">
                      <tr>
                        <th className="text-left p-3">Job</th>
                        <th className="text-left p-3">Client</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-right p-3">Revenue</th>
                        <th className="text-right p-3">Costs</th>
                        <th className="text-right p-3">Margin</th>
                        <th className="text-right p-3">%</th>
                        <th className="text-right p-3">Cash</th>
                        <th className="text-right p-3">Owing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(marginsReport?.jobs || []).map((row) => (
                        <tr key={row.job_id} className="border-t border-border hover:bg-muted/20">
                          <td className="p-3">
                            <a href={`/jobs/${row.job_id}`} className="text-primary hover:underline">{row.title}</a>
                            <div className="text-xs text-muted-foreground mt-0.5">{row.address}</div>
                          </td>
                          <td className="p-3">{row.client_name}</td>
                          <td className="p-3 capitalize">{(row.status || '').replace('_', ' ')}</td>
                          <td className="p-3 text-right font-mono">${row.revenue.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                          <td className="p-3 text-right font-mono">${row.costs.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                          <td className={`p-3 text-right font-mono font-bold ${row.margin >= 0 ? 'text-green-400' : 'text-destructive'}`}>${row.margin.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                          <td className="p-3 text-right">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-sm ${row.margin_pct >= 20 ? 'bg-green-500/20 text-green-400' : row.margin_pct >= 10 ? 'bg-yellow-500/20 text-yellow-400' : row.margin_pct >= 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'}`}>
                              {row.margin_pct.toFixed(1)}%
                            </span>
                          </td>
                          <td className="p-3 text-right font-mono text-green-400">${(row.cash_collected || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                          <td className={`p-3 text-right font-mono ${(row.outstanding || 0) > 0 ? 'text-orange-400' : 'text-muted-foreground'}`}>${(row.outstanding || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      {(!marginsReport?.jobs || marginsReport.jobs.length === 0) && (
                        <tr><td colSpan="9" className="p-8 text-center text-muted-foreground">No jobs in this range</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default ReportsPage;
